/**
 * Where things go on the Git view's branch map.
 *
 * Work flows left to right in three columns — the worktree branches, the
 * integration branch they land on, the base it lands on — and each column is
 * a band, the way the Architecture map groups its layers. The bands are all
 * as tall as the tallest column so their titles line up, and a lone
 * integration or base card sits level with the middle of the worktree stack,
 * so the lines into it fan in evenly rather than all bending up to the top.
 *
 * Pure and separate from the view so it can be tested without a DOM.
 */
import { BAND_HEAD, BAND_PAD, type FlowRect } from '../flowStyle';

/** A branch card: fixed, so the rows stay even whatever a card has to say. */
export const BRANCH_CARD = { width: 240, height: 88 } as const;
/** Gap between two cards in a column. */
export const BRANCH_ROW_GAP = 14;
/** Left edge of each column. */
export const BRANCH_COLUMN_X = { work: 0, integration: 360, base: 720 } as const;

export type BranchColumn = keyof typeof BRANCH_COLUMN_X;

export interface BranchMapInput<B> {
  base?: B;
  integration?: B;
  /** The work branches, in display order. */
  work: readonly B[];
}

export interface BranchMapLayout<B> {
  cards: Array<{ branch: B; column: BranchColumn; x: number; y: number }>;
  bands: Array<{ column: BranchColumn; rect: FlowRect; count: number }>;
}

export function layoutBranchMap<B>({ base, integration, work }: BranchMapInput<B>): BranchMapLayout<B> {
  const { width, height } = BRANCH_CARD;
  const stack = work.length ? work.length * height + (work.length - 1) * BRANCH_ROW_GAP : height;
  const middle = Math.round((stack - height) / 2);

  const cards: BranchMapLayout<B>['cards'] = work.map((branch, i) => ({ branch, column: 'work', x: BRANCH_COLUMN_X.work, y: i * (height + BRANCH_ROW_GAP) }));
  if (integration) cards.push({ branch: integration, column: 'integration', x: BRANCH_COLUMN_X.integration, y: middle });
  if (base) cards.push({ branch: base, column: 'base', x: BRANCH_COLUMN_X.base, y: middle });

  const bands: BranchMapLayout<B>['bands'] = [];
  for (const column of ['work', 'integration', 'base'] as const) {
    const count = cards.filter((c) => c.column === column).length;
    if (!count) continue;
    bands.push({ column, count, rect: { x: BRANCH_COLUMN_X[column] - BAND_PAD, y: -BAND_HEAD, width: width + BAND_PAD * 2, height: BAND_HEAD + stack + BAND_PAD } });
  }
  return { cards, bands };
}
